import { VendorClientResponse } from "@/Contracts/Vendor/Clients";
import {
	Client,
	ClientContactBulkUpdateRequest,
	ClientContactBulkUpdateResponse,
	ClientCount,
	ClientsRequest
} from "@/Contracts/Clients";
import { Address, ZomentumEntities } from "@/Contracts/common";
import { MergeTagVariable } from "@/Contracts/Documents";
import {
	CustomTemplateType,
	EmailTemplate
} from "@/Contracts/EmailTemplates";
import {
	OpportunityResponse,
	OpportunityView
} from "@/Contracts/Opportunity";
import { Pipeline } from "@/Contracts/Pipelines";
import { Integrations } from "@/Contracts/Settings";
import {
	Template,
	TemplateListRequest,
	TemplateOwnerType,
	TemplatesCount
} from "@/Contracts/Templates";
import { ListPagePostRequest } from "@/Contracts/UI";
import {
	BillingInfo,
	ConfigurationSettingsResponse,
	UserCompany,
	UserData,
	UserEmailSignature
} from "@/Contracts/Users";
import { VendorOpportunityResponse } from "@/Contracts/Vendor/Opportunity";
import { VendorPartnerResponse } from "@/Contracts/Vendor/Partner";
import { generateStore } from "@/Utils/Store";
import React, { memo } from "react";
import { combineReducers } from "redux";
import { ParentContextProvider } from "./context/ParentContext";
import { IParentContextProps } from "./context/ParentContext/interface";
import SalesActivityReducer, {
	SalesActivityState
} from "./reducers/SalesActivity";
import VendorClientReducer, {
	VendorClientState
} from "./reducers/Vendor/Client";
import VendorOpportunityReducer, {
	VendorOpportunityState
} from "./reducers/Vendor/Opportunity";
import VendorPartnerReducer, {
	VendorPartnerState
} from "./reducers/Vendor/Partner";
import VendorCommissionsReducer, {
	VendorComissionState
} from "./reducers/Vendor/Comissions";

export interface SalesActivityModuleState {
	salesActivity: SalesActivityState;
	vendorOpportunity: VendorOpportunityState;
	vendorPartner: VendorPartnerState;
	vendorClient: VendorClientState;
	vendorCommissions: VendorComissionState;
}

export const SalesActivityModuleReducer = combineReducers({
	salesActivity: SalesActivityReducer,
	vendorOpportunity: VendorOpportunityReducer,
	vendorPartner: VendorPartnerReducer,
	vendorClient: VendorClientReducer,
	vendorCommissions: VendorCommissionsReducer
});

const {
	withProvider: withSalesActivityModuleProvider,
	store: salesActivityModuleStore,
	useDispatch: useSalesActivityModuleDispatch,
	useSelector: useSalesActivityModuleSelector
} = generateStore<SalesActivityModuleState>(SalesActivityModuleReducer);

const defaultParentContext: IParentContextProps = {
	opportunity: {
		isLoading: false,
		opportunityList: [],
		currentActiveOpportunity: null,
		addOpportunityDrawerOpen: false,
		editOpportunityDrawerOpen: false,
		activeOpportunityView: OpportunityView.Kanban,
		showOpportunitiesLoader: function (): void {
			throw new Error("Function not implemented.");
		},
		hideOpportunitiesLoader: function (): void {
			throw new Error("Function not implemented.");
		},
		addOpportunityDrawerOpened: function (): void {
			throw new Error("Function not implemented.");
		},
		addOpportunityDrawerClosed: function (): void {
			throw new Error("Function not implemented.");
		},
		editOpportunityDrawerOpened: function (): void {
			throw new Error("Function not implemented.");
		},
		editOpportunityDrawerClosed: function (): void {
			throw new Error("Function not implemented.");
		},
		syncKanbanOpportunityDataById: function (
			_opportunityId: string
		): Promise<void> {
			throw new Error("Function not implemented.");
		},
		resetOpportunityDetails: function (): void {
			throw new Error("Function not implemented.");
		},
		getOpportunityListPost: function (
			_opportunityRequest: ListPagePostRequest
		): Promise<OpportunityResponse | VendorOpportunityResponse> {
			throw new Error("Function not implemented.");
		},
		ZOpportunitySelect: () => null,
		AddOpportunityDrawer: () => null
	},
	pipeline: {
		pipelineList: [],
		getAllPipelines: function (): Promise<{
			pipelineList: Pipeline[];
		}> {
			throw new Error("Function not implemented.");
		}
	},
	client: {
		isLoading: false,
		clientList: [],
		clientContactList: [],
		addClientDrawerOpen: false,
		editClientDrawerOpen: false,
		addClientContactDrawerOpen: false,
		currentActiveClient: null,
		showClientsLoader: function (): void {
			throw new Error("Function not implemented.");
		},
		hideClientsLoader: function (): void {
			throw new Error("Function not implemented.");
		},
		getClients: function (_clientsRequest: ClientsRequest): Promise<{
			clientList: Client[];
			clientCount: ClientCount;
		}> {
			throw new Error("Function not implemented.");
		},
		resetClients: function (): void {
			throw new Error("Function not implemented.");
		},
		resetClientContacts: function (): void {
			throw new Error("Function not implemented.");
		},
		resetClientDetails: function (): void {
			throw new Error("Function not implemented.");
		},
		addClientDrawerOpened: function (): void {
			throw new Error("Function not implemented.");
		},
		editClientDrawerOpened: function (): void {
			throw new Error("Function not implemented.");
		},
		addClientDrawerClosed: function (): void {
			throw new Error("Function not implemented.");
		},
		editClientDrawerClosed: function (): void {
			throw new Error("Function not implemented.");
		},
		addClientContactDrawerOpened: function (): void {
			throw new Error("Function not implemented.");
		},
		addClientContactDrawerClosed: function (): void {
			throw new Error("Function not implemented.");
		},
		bulkUpdateClientContactEmail: function (
			_bulkUpdateRequest: ClientContactBulkUpdateRequest
		): Promise<{
			results: ClientContactBulkUpdateResponse[];
		}> {
			throw new Error("Function not implemented.");
		},
		ZClientSelect: () => null,
		ZClientContactSelect: () => null,
		AddClientDrawer: () => null,
		AddClientContactDrawer: () => null
	},
	vendorPartner: {
		currentActivePartner: null,
		getPartnerListPagePost: function (
			_PartnerRequest: ListPagePostRequest
		): Promise<VendorPartnerResponse> {
			throw new Error("Function not implemented.");
		},
		ZVendorPartnerSelect: () => null,
		editVendorPartnerDrawerOpen: false,
		showEditVendorPartnerDrawer: function (): void {
			throw new Error("Function not implemented.");
		},
		hideEditVendorPartnerDrawer: function (): void {
			throw new Error("Function not implemented.");
		}
	},
	vendorComissions: {
		editVendorComissionsDrawerOpen: false,
		showEditVendorComissionsDrawer: function (): void {
			throw new Error("Function not implemented.");
		},
		hideEditVendorComissionsDrawer: function (): void {
			throw new Error("Function not implemented.");
		}
	},
	vendorClient: {
		currentActiveVendorClient: null,
		getClientListPagePost: function (
			_ClientRequest: ListPagePostRequest
		): Promise<VendorClientResponse> {
			throw new Error("Function not implemented.");
		},
		ZVendorClientSelect: () => null,
		editVendorClientDrawerOpen: false,
		showEditVendorClientDrawer: function (): void {
			throw new Error("Function not implemented.");
		},
		hideEditVendorClientDrawer: function (): void {
			throw new Error("Function not implemented.");
		}
	},
	template: {
		isLoading: false,
		templateList: [],
		templatesCount: new TemplatesCount(),
		getTemplates: function (
			_templateListRequest: TemplateListRequest,
			_templateOwnerType: TemplateOwnerType
		): Promise<{
			templateList: Template[];
			templateCount: TemplatesCount;
		}> {
			throw new Error("Function not implemented.");
		},
		showTemplatesLoader: function (): void {
			throw new Error("Function not implemented.");
		},
		hideTemplatesLoader: function (): void {
			throw new Error("Function not implemented.");
		}
	},
	recentActivityFeed: {
		isLoading: false,
		emailThreadDrawerOpen: false,
		emailThreadDrawerOpened: function (): void {
			throw new Error("Function not implemented.");
		},
		emailThreadDrawerClosed: function (): void {
			throw new Error("Function not implemented.");
		}
	},
	emailThread: {
		isLoading: false,
		emailThreadMessageDrawerOpen: false,
		missingEmailModalOpen: false,
		emailMergeTagsSystemVariable: [],
		emailThreadMessageDrawerOpened: function (
			_sendEmailWithoutDocument?: boolean
		): void {
			throw new Error("Function not implemented.");
		},
		emailThreadMessageDrawerClosed: function (): void {
			throw new Error("Function not implemented.");
		},
		missingEmailModalOpened: function (): void {
			throw new Error("Function not implemented.");
		},
		missingEmailModalClosed: function (): void {
			throw new Error("Function not implemented.");
		},
		resetMergeTags: function (): void {
			throw new Error("Function not implemented.");
		},
		fetchMergeTagsWithoutId: function (_entityType: string): Promise<void> {
			throw new Error("Function not implemented.");
		},
		EmailThreadComponent: () => null,
		EmailThreadMessageDrawer: () => null,
		ImportEmailTemplateModal: () => null,
		TrackEmail: () => null,
		EmailSignatureComponent: () => null
	},
	document: {
		addDocumentDrawerOpen: false,
		editOrCloneDocumentDrawerOpen: false,
		sendDocumentDrawerOpen: false,
		addDocumentDrawerOpened: function (): void {
			throw new Error("Function not implemented.");
		},
		editOrCloneDocumentDrawerOpened: function (): void {
			throw new Error("Function not implemented.");
		},
		sendDocumentDrawerOpened: function (): void {
			throw new Error("Function not implemented.");
		},
		addDocumentDrawerClosed: function (): void {
			throw new Error("Function not implemented.");
		},
		editOrCloneDocumentDrawerClosed: function (): void {
			throw new Error("Function not implemented.");
		},
		sendDocumentDrawerClosed: function (): void {
			throw new Error("Function not implemented.");
		}
	},
	user: {
		isLoading: false,
		userData: new UserData(),
		subscriptions: [],
		taxRegionsList: [],
		configurationSettings: new ConfigurationSettingsResponse(),
		billingInfo: new BillingInfo(),
		userCompany: new UserCompany(),
		companyUserList: [],
		emailSignatures: new UserEmailSignature(),
		getUserCompany: function (): Promise<void> {
			throw new Error("Function not implemented.");
		},
		ZUserSelect: () => null,
		UserSelectComponent: () => null
	},
	emailTemplate: {
		fetchTemplateByType: function (
			_customTemplateType: CustomTemplateType
		): Promise<{
			templates: EmailTemplate[]; // TODO: Change later when filter is introduced in the backend
		}> {
			throw new Error("Function not implemented.");
		}
	},
	setting: {
		isLoading: false,
		appUpdateBannerVisible: false,
		customFieldList: [],
		getCustomFields: function (
			_entityType: ZomentumEntities
		): Promise<void> {
			throw new Error("Function not implemented.");
		},
		isFormEdited: null,
		setFormEdited: function (_formKey: string): void {
			throw new Error("Function not implemented.");
		},
		resetFormEdited: function (_formKey: string): void {
			throw new Error("Function not implemented.");
		}
	},
	misc: {
		isBannerOpen: false,
		vendorPromotionBannerDismissed: undefined,
		fetchMergeTagsWithNoEntityId: function (
			_entityType: string
		): Promise<MergeTagVariable[]> {
			throw new Error("Function not implemented.");
		},
		getShippingAddressesListItems: function (_props: {
			isDisabled?: boolean;
			isDocumentLevel?: boolean;
			address: Address;
			id: string;
		}): {
			name: JSX.Element;
			value: string;
		} {
			throw new Error("Function not implemented.");
		},
		ZTableWithDecorators: (_props: any) => {
			return <></>;
		},
		Link: () => null,
		historyPush: function (_location: string): void {
			throw new Error("Function not implemented.");
		}
	},
	integrations: new Integrations()
};

const SalesActivityProvider: React.FC<IParentContextProps> = memo(
	({ children, ...rest }) => {
		return (
			<ParentContextProvider {...rest}>{children}</ParentContextProvider>
		);
	}
);

export {
	SalesActivityProvider,
	withSalesActivityModuleProvider,
	salesActivityModuleStore,
	useSalesActivityModuleDispatch,
	useSalesActivityModuleSelector,
	defaultParentContext
};
